import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { Shield, AlertCircle, ArrowLeft, UserPlus } from '../components/icons';
import { supabase } from '../lib/supabase';
import { verifyAssessmentCode } from '../lib/assessment-codes';
import { checkUserRole } from '../lib/auth';
import { useAuth } from '../contexts/AuthContext';
import { autoCreateLead } from '../lib/crm';

export function RegistrationPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    fullName: '',
    assessmentCode: '',
    privacyAccepted: false,
    termsAccepted: false,
    disclaimerAccepted: false,
    gdprAccepted: false,
    marketingAccepted: false
  });

  useEffect(() => {
    if (!user) return;

    (async () => {
      try {
        const role = await checkUserRole(user.id);
        switch (role) {
          case 'admin':
            navigate('/admin');
            break;
          case 'partner':
            navigate('/partner');
            break;
          case 'coach':
            navigate('/coach');
            break;
          case 'respondent':
            navigate('/dashboard');
            break;
        }
      } catch (error) {
        console.error('Error checking user role:', error);
      }
    })();
  }, [user, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const { isValid, coachId, teamId, autoAiAnalysis } = await verifyAssessmentCode(formData.assessmentCode.trim());
      if (!isValid) {
        throw new Error('Invalid assessment code. Please check and try again.');
      }

      const { data: authData, error: signUpError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            full_name: formData.fullName,
            role: 'respondent',
          },
        },
      });

      if (signUpError) throw signUpError;
      if (!authData.user) throw new Error('Registration failed');

      const { error: userError } = await supabase
        .from('users')
        .update({ full_name: formData.fullName })
        .eq('id', authData.user.id);

      if (userError) throw userError;

      const { error: agreementError } = await supabase.from('user_agreements').insert({
        user_id: authData.user.id,
        privacy_accepted: formData.privacyAccepted,
        privacy_accepted_at: new Date().toISOString(),
        terms_accepted: formData.termsAccepted,
        terms_accepted_at: new Date().toISOString(),
        disclaimer_accepted: formData.disclaimerAccepted,
        disclaimer_accepted_at: new Date().toISOString(),
        gdpr_accepted: formData.gdprAccepted,
        gdpr_accepted_at: new Date().toISOString(),
      });

      if (agreementError) throw agreementError;

      const { error: respondentError } = await supabase.from('respondents').insert({
        user_id: authData.user.id,
        coach_id: coachId || null,
        ai_analysis_enabled: autoAiAnalysis || false,
        ai_analysis_visible: autoAiAnalysis || false,
      });

      if (respondentError) throw respondentError;

      if (coachId) {
        const codeValue = formData.assessmentCode.trim();
        if (teamId) {
          // Team code: reusable, increment use_count and auto-join team
          await (supabase.rpc as any)('increment_code_use_count', { code_value: codeValue });
          await supabase.from('team_members').insert({
            team_id: teamId,
            user_id: authData.user.id,
          });
        } else {
          // Individual code: single-use, mark as used
          await supabase
            .from('assessment_codes')
            .update({ is_used: true, used_by: authData.user.id, used_at: new Date().toISOString() })
            .eq('code', codeValue);
        }
      }

      // Auto-create CRM lead for the coach
      if (coachId) {
        autoCreateLead(coachId, {
          full_name: formData.fullName,
          email: formData.email,
          source: 'assessment_completion',
          user_id: authData.user.id,
        }).catch(() => {});
      }

      let coachName = '';
      if (coachId) {
        const { data: coachUser } = await supabase
          .from('coaches')
          .select('user_id')
          .eq('id', coachId)
          .maybeSingle();
        if (coachUser) {
          const { data: coachData } = await supabase
            .from('users')
            .select('full_name')
            .eq('id', coachUser.user_id)
            .maybeSingle();
          coachName = coachData?.full_name || '';
        }
      }

      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-email`;
      fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          type: 'welcome-respondent',
          recipientEmail: formData.email,
          recipientName: formData.fullName,
          coachName,
        }),
      }).catch(() => {});

      navigate('/instructions');

    } catch (err: any) {
      console.error('Registration error:', err);
      setError(err.message || 'An error occurred during registration');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen freud-bg py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-chocolate-600 hover:text-sage-600 mb-8 transition-colors font-medium"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Home
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="flex justify-center mx-auto mb-4">
            <img src="/human_potential_os_logo_V2_cropped.png" alt="Human Potential OS" className="h-16 w-auto" />
          </div>
          <h1 className="freud-heading-2xl text-chocolate-900 mb-2">
            Register for the Assessment
          </h1>
          <p className="freud-text-base text-chocolate-500">
            Create your account and discover yourself!
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="freud-card-elevated p-8"
        >
          {error && (
            <div className="flex items-center gap-2 bg-warm-100 text-warm-700 p-4 rounded-xl mb-6">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <p className="text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="assessmentCode" className="freud-label block text-chocolate-800 mb-2">Assessment Code</label>
              <input
                type="text"
                id="assessmentCode"
                name="assessmentCode"
                value={formData.assessmentCode}
                onChange={handleChange}
                required
                className="freud-input"
                disabled={isLoading}
                placeholder="Enter your assessment code"
              />
            </div>

            <div>
              <label htmlFor="fullName" className="freud-label block text-chocolate-800 mb-2">Full Name</label>
              <input
                type="text"
                id="fullName"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                required
                className="freud-input"
                disabled={isLoading}
                placeholder="Enter your full name"
              />
            </div>

            <div>
              <label htmlFor="email" className="freud-label block text-chocolate-800 mb-2">Email Address</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="freud-input"
                disabled={isLoading}
                placeholder="Enter your email address"
              />
            </div>

            <div>
              <label htmlFor="password" className="freud-label block text-chocolate-800 mb-2">Password</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                minLength={8}
                className="freud-input"
                disabled={isLoading}
                placeholder="Choose a secure password (min. 8 characters)"
              />
            </div>

            <div className="space-y-4 pt-4 border-t border-cream-300">
              <p className="freud-label text-chocolate-700">Required Agreements</p>

              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="privacyAccepted"
                  name="privacyAccepted"
                  checked={formData.privacyAccepted}
                  onChange={handleChange}
                  required
                  className="mt-1 w-4 h-4 rounded border-cream-400 text-sage-600 focus:ring-sage-500"
                  disabled={isLoading}
                />
                <label htmlFor="privacyAccepted" className="text-chocolate-600 text-sm leading-relaxed">
                  I have read and agree to the <Link to="/privacy" className="text-sage-600 hover:text-sage-700 font-medium">Privacy Policy</Link> and <Link to="/privacy" className="text-sage-600 hover:text-sage-700 font-medium">GDPR</Link> and understand how my personal data will be processed.
                </label>
              </div>

              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="termsAccepted"
                  name="termsAccepted"
                  checked={formData.termsAccepted}
                  onChange={handleChange}
                  required
                  className="mt-1 w-4 h-4 rounded border-cream-400 text-sage-600 focus:ring-sage-500"
                  disabled={isLoading}
                />
                <label htmlFor="termsAccepted" className="text-chocolate-600 text-sm leading-relaxed">
                  I accept the <Link to="/terms" className="text-sage-600 hover:text-sage-700 font-medium">Terms of Service</Link> and agree to be bound by its conditions.
                </label>
              </div>

              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="disclaimerAccepted"
                  name="disclaimerAccepted"
                  checked={formData.disclaimerAccepted}
                  onChange={handleChange}
                  required
                  className="mt-1 w-4 h-4 rounded border-cream-400 text-sage-600 focus:ring-sage-500"
                  disabled={isLoading}
                />
                <label htmlFor="disclaimerAccepted" className="text-chocolate-600 text-sm leading-relaxed">
                  I acknowledge the <Link to="/disclaimer" className="text-sage-600 hover:text-sage-700 font-medium">Disclaimer & Indemnity</Link> and understand the assessment's scope and limitations.
                </label>
              </div>

              <div className="flex items-start gap-3 bg-cream-100 p-4 rounded-xl">
                <input
                  type="checkbox"
                  id="gdprAccepted"
                  name="gdprAccepted"
                  checked={formData.gdprAccepted}
                  onChange={handleChange}
                  required
                  className="mt-1 w-4 h-4 rounded border-cream-400 text-sage-600 focus:ring-sage-500"
                  disabled={isLoading}
                />
                <label htmlFor="gdprAccepted" className="text-chocolate-600 text-sm leading-relaxed">
                  By proceeding, I acknowledge that the Human Potential OS is a coaching and personal development tool intended solely for informational and educational purposes. It is not a psychological, medical, or diagnostic test, and its results do not diagnose, treat, or predict any psychological, medical, or health conditions. I understand that the results are based on my responses and the profile's methodology and may not guarantee accuracy, completeness, or applicability. I agree to use the results at my sole discretion and assume full responsibility for any decisions made. I hereby release, indemnify, and hold harmless the creators, affiliates, independent practitioners, and any associated parties of the Human Potential OS from all claims, liabilities, damages, or losses, including direct, indirect, or consequential damages, arising out of or related to my use of this profile and its results.
                </label>
              </div>

              <div className="flex items-center justify-between pt-6">
                <div className="flex items-center gap-2 text-chocolate-500">
                  <Shield className="w-4 h-4 text-sage-600" />
                  <span className="text-sm">Secure Registration</span>
                </div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="freud-btn-primary inline-flex items-center gap-2"
                >
                  <UserPlus className="w-5 h-5" />
                  {isLoading ? 'Creating Account...' : 'Create Account'}
                </button>
              </div>
            </div>
          </form>

          <div className="mt-8 pt-6 border-t border-cream-300 text-center">
            <p className="text-chocolate-500 text-sm">
              Already have an account?{" "}
              <Link to="/login" className="text-coral-600 hover:text-coral-700 font-medium">
                Sign in here
              </Link>
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
